import type {
    HostEntityTabInstance,
    ConfigurableTabInstance,
} from '@microsoft/teams-js/dist/esm/packages/teams-js/dts/private/hostEntity/tab';
import type { StageViewParams } from '@microsoft/teams-js/dist/esm/packages/teams-js/dts/public/stageView/stageView';
import type { PerformanceDatapoint } from 'owa-analytics';
import type { OpenGroupChatRequest, OpenSingleChatRequest } from '@microsoft/teams-js';
import type { OnTabUnLoad, OnTabLoaded, LoadAndHostDataForTelemetry } from './appCaching.interface';
import type { Context } from '@microsoft/teams-js/dist/esm/packages/teams-js/dts/public/app/app';
import type {
    PersonaIdentifiers,
    TriggerType,
    Modality,
} from '@microsoft/teams-js/dist/esm/packages/teams-js/dts/public/profile';
import type { HeadersWithoutIterator } from 'owa-service/lib/RequestOptions';
import type { MailboxInfo } from 'owa-client-types';
import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyBootModule } from 'owa-bundling-light';

export const lazyModule: LazyBootModule<typeof lazyIndex> = new LazyBootModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "PlatformAppBoot"*/ './lazyIndex'),
    { name: 'PlatformAppBoot' }
);

export const lazyGetOwaAuthTokenFromMetaOsHub: LazyAction<
    (
        headers?: HeadersWithoutIterator | undefined,
        _mailboxInfo?: MailboxInfo | undefined
    ) => Promise<string | undefined>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getOwaAuthTokenFromMetaOsHub);

export const lazyGetOwaDecodedAuthTokenFromMetaOsHub: LazyAction<
    () => Promise<{
        exp: number;
        puid: string;
    }>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getOwaDecodedAuthTokenFromMetaOsHub);

export const lazyGetAuthTokenFromMetaOsHub: LazyAction<
    (resource: string, headers?: HeadersWithoutIterator) => Promise<string>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getAuthTokenFromMetaOsHub);

export const lazyOpenCalendarItem: LazyAction<
    (itemId: string) => Promise<string>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openCalendarItem);

export const lazyComposeMeeting: LazyAction<
    (
        attendees?: string[],
        startTime?: string,
        endTime?: string,
        subject?: string,
        content?: string
    ) => Promise<string>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.composeMeeting);

export const lazyOpenProfileItem: LazyAction<
    (
        identifiers: PersonaIdentifiers,
        triggerType: TriggerType,
        targetElementBoundingRect?: DOMRect,
        modality?: Modality
    ) => Promise<string>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openProfileItem);

export const lazyOpenMailItem: LazyAction<
    (itemId: string) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openMailItem);

export const lazyComposeMail: LazyAction<
    (
        toRecipients?: string[],
        ccRecipients?: string[],
        bccRecipients?: string[],
        subject?: string,
        message?: string
    ) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.composeMail);
export const lazyReplyMail: LazyAction<
    (itemId: string) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.replyMail);
export const lazyReplyAllMail: LazyAction<
    (itemId: string) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.replyAllMail);
export const lazyForwardMail: LazyAction<
    (itemId: string) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.forwardMail);

export const lazyExecuteDeepLink: LazyAction<
    (deepLink: string) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.executeDeepLink);

export const lazyGetContext: LazyAction<
    () => Promise<Context>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getContext);

export const lazyCheckCalendarCapability: LazyAction<
    typeof lazyIndex.checkCalendarCapability,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.checkCalendarCapability);

export const lazyCheckMailCapability: LazyAction<
    typeof lazyIndex.checkMailCapability,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.checkMailCapability);

export const lazyCheckDialogCapability: LazyAction<
    typeof lazyIndex.checkDialogCapability,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.checkDialogCapability);

export const lazyIsDarkTheme: LazyAction<
    typeof lazyIndex.isDarkTheme,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.isDarkTheme);

export const lazyJoinTeamsMeeting: LazyAction<
    typeof lazyIndex.joinTeamsMeeting,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.joinTeamsMeeting);

export const lazyModalOpened: LazyAction<
    typeof lazyIndex.modalOpened,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.modalOpened);

export const lazySubmitTask: LazyAction<
    typeof lazyIndex.submitTask,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.submitTask);

export const lazyNotifySuccess: LazyAction<
    typeof lazyIndex.notifySuccess,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.notifySuccess);

export const lazyNotifyFailure: LazyAction<
    typeof lazyIndex.notifyFailure,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.notifyFailure);
export const lazyNotifyOtherFailure: LazyAction<
    typeof lazyIndex.notifyOtherFailure,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.notifyOtherFailure);

export const lazyNotifyAppLoaded: LazyAction<
    typeof lazyIndex.notifyAppLoaded,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.notifyAppLoaded);

export const lazyRegisterIsDarkThemeOnChangeHandler: LazyAction<
    (callback: (isDarkTheme: boolean) => void) => void,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.registerIsDarkThemeOnChangeHandler);

export const lazyInitializeMetaOsCapabilities: LazyAction<
    typeof lazyIndex.initializeMetaOsCapabilities,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.initializeMetaOsCapabilities);

export const lazyHandleThemeChange: LazyAction<
    (themeChangeHandler: (theme: string) => void) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.handleThemeChange);

export const lazyIsMetaOsAppSdkInitialized: LazyAction<
    typeof lazyIndex.isMetaOsAppSdkInitialized,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.isMetaOsAppSdkInitialized);

export const lazyInitializeMetaOsApp: LazyAction<
    typeof lazyIndex.initializeMetaOSApp,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.initializeMetaOSApp);

export const lazyInitializeMetaOsAppSdk: LazyAction<
    typeof lazyIndex.initializeMetaOsAppSdk,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.initializeMetaOsAppSdk);

export const lazyRegisterOnLoadHandler: LazyAction<
    (onTabLoadedObj?: OnTabLoaded) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.registerOnLoadHandler);
export const lazyRegisterOnCachedLoadCompleted: LazyAction<
    (callback: (infoForTelemetry: LoadAndHostDataForTelemetry) => void) => void,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.registerOnCachedLoadCompleted);
export const lazyRegisterBeforeUnloadHandler: LazyAction<
    (onBeforeUnloadObj?: OnTabUnLoad) => Promise<void>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.registerBeforeUnloadHandler);

export const lazyRemoveAppCachingQueryParam: LazyAction<
    typeof lazyIndex.removeAppCachingQueryParam,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.removeAppCachingQueryParam);

export const lazyStartCall: LazyAction<
    (startCallParams: { targets: string[]; isVideo?: boolean }) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.startCall);
export const lazyOpenChat: LazyAction<
    (openChatRequest: OpenSingleChatRequest) => Promise<void>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openChat);
export const lazyOpenGroupChat: LazyAction<
    (openGroupChatRequest: OpenGroupChatRequest) => Promise<void>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openGroupChat);
export const lazyOpenDialog: LazyAction<
    typeof lazyIndex.open,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.open);
export const lazyCloseDialog: LazyAction<
    typeof lazyIndex.close,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.close);
export const lazyPreInitOwaAccountSourceListStore: LazyAction<
    typeof lazyIndex.preInitOwaAccountSourceListStore,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.preInitOwaAccountSourceListStore);
export const lazyGetAccountServiceConfig: LazyAction<
    typeof lazyIndex.getAccountServiceConfig,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getAccountServiceConfig);
export const lazyNavigateToApp: LazyAction<
    (appId: string, pageId: string, subPageId?: string) => Promise<void>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.navigateToApp);
export const lazyJoinMeeting: LazyAction<
    (joinWebUrl: string, perfDatapoint: PerformanceDatapoint, source?: string) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.joinMeeting);
export const lazyGetCachedContext: LazyAction<
    () => Promise<Context>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.getCachedContext);
export const lazyIsNAAChannelRecommended: LazyAction<
    typeof lazyIndex.isNAAChannelRecommended,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.isNAAChannelRecommended);
export const lazyOpenStageView: LazyAction<
    (stageViewParams: StageViewParams) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openStageView);
export const lazyCloseStageView: LazyAction<
    () => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.closeStageView);
export const lazyCheckStageViewCapability: LazyAction<
    typeof lazyIndex.checkStageViewCapability,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.checkStageViewCapability);

export const lazyRemoveTab: LazyAction<
    (
        appId: string,
        tabId: string,
        tabName: string,
        tabType: 'ConfigurableTab' | 'StaticTab',
        threadId: string,
        parentMessageId?: string
    ) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.removeTab);

export const lazyRenameTab: LazyAction<
    (
        appId: string,
        tabId: string,
        tabName: string,
        threadId: string,
        parentMessageId?: string
    ) => Promise<ConfigurableTabInstance>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.renameTab);

export const lazyReconfigureTab: LazyAction<
    (
        appId: string,
        tabId: string,
        tabName: string,
        threadId: string,
        parentMessageId?: string
    ) => Promise<ConfigurableTabInstance>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.reconfigureTab);
export const lazyOpenCreateTabFlyout: LazyAction<
    (threadId: string, messageId?: string, appType?: string) => Promise<HostEntityTabInstance>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.openCreateTabFlyout);

export const lazyHandleHostSearch: LazyAction<
    (
        searchExecuteHandler: (query: string) => void,
        searchCloseHandler: (query: string) => void,
        queryChangedHandler?: (query: string) => void
    ) => Promise<boolean>,
    LazyBootModule<typeof lazyIndex>
> = new LazyAction(lazyModule, m => m.handleHostSearch);
